import * as React from 'react';
import { cn } from '@/lib/utils';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import styles from './badge.module.css';

const VARIANT_CLASS = {
  default: styles.variantDefault,
  secondary: styles.variantSecondary,
  outline: styles.variantOutline,
  success: styles.variantSuccess,
  warning: styles.variantWarning,
  destructive: styles.variantDestructive,
  info: styles.variantInfo,
  attention: styles.variantAttention,
} as const;

export type BadgeVariant = keyof typeof VARIANT_CLASS;

/** Badge class string, for elements other than `Badge` that must look like one. */
function badgeVariants({ variant }: { variant?: BadgeVariant | null } = {}): string {
  return cn(styles.root, VARIANT_CLASS[variant ?? 'default']);
}

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement> {
  variant?: BadgeVariant | null;
  /** Play the attention wiggle once on mount (attention variant only). */
  animateAttention?: boolean;
}

function Badge({ className, variant, animateAttention = false, ...props }: BadgeProps) {
  const reducedMotion = useReducedMotion();
  const shouldWiggle =
    variant === 'attention' && animateAttention && !reducedMotion;

  return (
    <div
      className={cn(
        badgeVariants({ variant }),
        shouldWiggle && styles.wiggle,
        className,
      )}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
